import { useLayoutEffect, useRef, useState } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, Eye, Check } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { useSelectedTour } from '@/context/TourContext'
import { OrgSwitcher } from '@/components/OrgSwitcher'
import { UserMenu } from '@/components/UserMenu'
import { ThemeToggle } from '@/components/ThemeToggle'
import { NotificationToggle } from '@/components/NotificationToggle'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { ScheduleView } from './ScheduleView'
import { TourSetup } from './TourSetup'
import { CrewView } from './CrewView'
import { withViewTransition } from '@/lib/viewTransition'
import { cn } from '@/lib/utils'

type Tab = 'schedule' | 'crew' | 'settings'

export default function ManagerPage() {
  const { selectedTour: tour, loading: isLoading } = useSelectedTour()
  const navigate = useNavigate()
  const location = useLocation()

  const activeTab: Tab = location.pathname.includes('/crew')
    ? 'crew'
    : location.pathname.includes('/settings')
    ? 'settings'
    : 'schedule'

  const tabs: { id: Tab; label: string; path: string }[] = [
    { id: 'schedule', label: 'Schedule', path: '/manager' },
    { id: 'crew', label: 'Crew', path: '/manager/crew' },
    { id: 'settings', label: 'Tours', path: '/manager/settings' },
  ]

  return (
    <div className="min-h-screen bg-background max-w-lg md:max-w-2xl lg:max-w-6xl mx-auto">
      {/* Sticky header — org + (lg+ centered) tabs + action icons.
          On lg+ the layout becomes a 3-col grid so the tab strip stays
          dead-center regardless of how wide the org pill or icon cluster
          render. Mobile keeps the simpler flex layout (no tabs here). */}
      <header className="scroll-header sticky top-0 z-10 px-4 lg:px-8 pt-safe">
        <div className="
          flex items-center justify-between h-14
          lg:h-16 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-4
        ">
          <div className="min-w-0 flex-shrink flex-grow-0 lg:justify-self-start">
            <OrgSwitcher />
          </div>
          <div className="hidden lg:block lg:justify-self-center">
            <TabStrip
              tabs={tabs}
              activeTab={activeTab}
              onSelect={(path) => withViewTransition(() => navigate(path))}
              variant="compact"
            />
          </div>
          <div className="icon-cluster shrink-0 lg:justify-self-end">
            <button
              onClick={() => navigate('/today')}
              aria-label="Crew view"
              title="View as crew"
            >
              <Eye className="w-[18px] h-[18px]" strokeWidth={2} />
            </button>
            <NotificationToggle />
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>

      {/* pb leaves room for the fixed 'Add activity' dock on the Schedule tab. */}
      <main
        className={cn(
          'px-4 lg:px-8 pt-2',
          activeTab === 'schedule' ? 'pb-[calc(88px+env(safe-area-inset-bottom,0px))]' : 'pb-safe'
        )}
      >
        <div className="max-w-2xl mx-auto">
          {/* Editorial hero — eyebrow + big title.
              '.compact' reduces vertical weight vs. the Today hero.
              On the Tours tab (lg+ only — mobile renders the compact list)
              the title is the literal word "Tours" instead of the selected
              tour name, since the grid below is the manager's all-tours
              surface. Keeping the hero slot occupied avoids a layout jump
              when switching between tabs. */}
          <div className="today-heading compact">
            <div className="today-eyebrow">
              <span>Manager</span>
            </div>
            {activeTab === 'settings' ? (
              <>
                <div className="lg:hidden"><TourTitle /></div>
                <h1 className="today-title hidden lg:block">Tours</h1>
              </>
            ) : (
              <TourTitle />
            )}
          </div>

          {/* Tabs with a sliding active pill. View transition also runs on
              navigate so the content cross-fades while the pill slides.
              On lg+ the same strip lives inside the sticky header (above),
              so we hide this instance to avoid duplication. */}
          <div className="lg:hidden mb-3">
            <TabStrip tabs={tabs} activeTab={activeTab} onSelect={(path) => withViewTransition(() => navigate(path))} />
          </div>
        </div>

        <div className="max-w-2xl mx-auto lg:max-w-none">
          <Routes>
            <Route index element={<ScheduleView tour={tour ?? null} loading={isLoading} />} />
            <Route path="crew" element={<CrewView />} />
            <Route path="settings" element={<TourSetup />} />
          </Routes>
        </div>
      </main>
    </div>
  )
}

// Sliding-pill tab strip. The '.manager-tab-indicator' pill is absolutely
// positioned; a useLayoutEffect measures the active tab and updates the
// indicator's --ind-left / --ind-width custom properties so CSS animates
// the transition with the spring easing. ResizeObserver keeps things
// correct when the viewport changes (e.g., rotation).
interface TabStripProps {
  tabs: { id: Tab; label: string; path: string }[]
  activeTab: Tab
  onSelect: (path: string) => void
  // 'default' = full-width tabs (flex: 1), used in the mobile content row.
  // 'compact' = auto-sized tabs with horizontal padding, used in the sticky
  // header on lg+ where the strip needs to size to its content.
  variant?: 'default' | 'compact'
}

function TabStrip({ tabs, activeTab, onSelect, variant = 'default' }: TabStripProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const [ind, setInd] = useState<{ left: number; width: number } | null>(null)

  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return

    const measure = () => {
      const active = list.querySelector<HTMLElement>('[data-active="true"]')
      if (active) {
        setInd({ left: active.offsetLeft, width: active.offsetWidth })
      }
    }

    measure()

    const ro = new ResizeObserver(measure)
    ro.observe(list)
    return () => ro.disconnect()
  }, [activeTab])

  return (
    <nav
      className={cn('manager-tabs', variant === 'compact' && 'manager-tabs--compact')}
      role="tablist"
      ref={listRef}
    >
      {ind && (
        <span
          className="manager-tab-indicator"
          style={{
            // px values applied via custom props so CSS `transition` can animate them
            ['--ind-left' as string]: `${ind.left}px`,
            ['--ind-width' as string]: `${ind.width}px`,
          }}
          aria-hidden="true"
        />
      )}
      {tabs.map(t => (
        <button
          key={t.id}
          role="tab"
          aria-selected={activeTab === t.id}
          data-active={activeTab === t.id}
          onClick={() => onSelect(t.path)}
          className={cn('manager-tab', activeTab === t.id && 'active')}
        >
          {t.label}
        </button>
      ))}
    </nav>
  )
}

// Big tour name as the hero title. Tappable when the user has more than
// one tour — opens a dropdown picker. Single-tour users see a static title.
function TourTitle() {
  const { tours, selectedTour, setSelectedTourId } = useSelectedTour()

  if (!selectedTour) {
    return <h1 className="today-title text-[color:var(--muted-foreground)]">No tour selected</h1>
  }

  if (tours.length <= 1) {
    return <h1 className="today-title">{selectedTour.nombre}</h1>
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="today-title inline-flex items-baseline gap-1.5 text-left hover:opacity-85 transition-opacity outline-none">
          <span className="truncate">{selectedTour.nombre}</span>
          <ChevronDown
            className="w-4 h-4 text-[color:var(--subtle-foreground)] shrink-0 translate-y-[-2px]"
            strokeWidth={2.5}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground/70 font-medium px-3 py-2">
          Switch tour
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tours.map(t => {
          const selected = t.id === selectedTour.id
          return (
            <DropdownMenuItem
              key={t.id}
              onClick={() => setSelectedTourId(t.id)}
              className="items-start cursor-pointer"
            >
              <div className="w-4 shrink-0 pt-0.5">
                {selected && <Check className="w-3.5 h-3.5 text-primary" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-medium truncate">{t.nombre}</p>
                  {t.estado === 'archived' && (
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
                      archived
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {format(parseISO(t.fecha_inicio), 'MMM d')} — {format(parseISO(t.fecha_fin), 'MMM d, yyyy')}
                </p>
              </div>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
